import React, { useRef, useContext } from 'react';
import { UserRole, getHighestUserRole, formatPhoneNumber } from '../../Constants/constants';
import { UserContext } from '../../Context/UserContext';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import EntityPage from '../../Layout/EntityPage';
import { IOverrideEntityActions } from '../../API/Interfaces/IEntityActions';
import { Query, QueryResult, Column } from 'material-table';
import ISearchBaseDto from '../../API/Interfaces/ISearchBaseDto';
import AppLink from '../../Layout/Components/AppLink';
import { routes } from '../../Routing/routes';
import { DealerAdminServiceAPI } from '../../API/Services/UserServicesAPI';
import { IUserViewDto } from '../../API/Interfaces/IUserDtos';

export const Users = () => {
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const tableRef = useRef();
	const userContext = useContext(UserContext);
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.ErrorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	let headers;

	const headersInfo: Array<Column<IUserViewDto>> = [
		{
			title: t('users.table.nameHeader'),
			field: 'name',
			render: (rowData) => <AppLink to={routes.userDetails.replace(':id', rowData.id)}>{rowData.name}</AppLink>,
		},
		{
			title: t('users.table.emailHeader'),
			field: 'email',
			sorting: false,
		},
		{
			title: t('users.table.phoneHeader'),
			field: 'phone',
			sorting: false,
			render: (rowData) => formatPhoneNumber(rowData.phone),
		},
		{
			title: t('users.table.roleHeader'),
			field: 'role',
			render: (rowData) => t(`users.roles.${rowData.role}`),
		},
		{
			title: t('users.table.dealerHeader'),
			field: 'dealer',
		},
		{
			title: t('users.table.corporationHeader'),
			field: 'corporation',
		},
		{
			title: t('users.table.locationHeader'),
			field: 'location',
		},
	];

	const highestUserRole: string = getHighestUserRole(userContext.userRoles);
	switch (highestUserRole) {
		case UserRole.LocationAdmin:
			headers = headersInfo.filter((t) => t.field !== 'location' && t.field !== 'corporation' && t.field !== 'dealer');
			break;
		case UserRole.CorporationAdmin:
			headers = headersInfo.filter((t) => t.field !== 'dealer' && t.field !== 'corporation');
			break;
		case UserRole.DealerAdmin:
			headers = headersInfo.filter((t) => t.field !== 'dealer');
			break;
		case UserRole.SuperAdmin:
			headers = headersInfo;
			break;
	}

	const EntityActions: IOverrideEntityActions<IUserViewDto, IUserViewDto> = {
		api: DealerAdminServiceAPI,
		presetDataCallback: (data: any) => {
			return data;
		},
		getTableData: async function (query: Query<IUserViewDto>) {
			const result: QueryResult<IUserViewDto> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await EntityActions.api.GetPage(request);
				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;
					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.ErrorOccured')}: ${message}`, { variant: 'error' });
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const tableProps: any = {
		columns: headers,
		options: { paging: true },
	};

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: t('menu.users'),
		tableProps: tableProps,
		disableAddNew: true,
		isDetails: false, //props.isInDetails,
		//firstColumn: props.isInDetails ? 'name' : undefined,
	};

	return <EntityPage {...entityPageProps} />;
};
